import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import SessionItem from "./SessionItem";
import { useSessionDeletion } from "../hooks/useSessionDeletion";

/**
 * GameHistory component for displaying game session history
 * @param {Object} props - Component props
 * @param {Array} props.sessions - Array of game sessions
 * @param {string} props.expandedSessionId - ID of currently expanded session
 * @param {string} props.hoveredSessionId - ID of currently hovered session
 * @param {Function} props.setHoveredSessionId - Function to set hovered session
 * @param {Function} props.toggleSessionDetail - Function to toggle session details
 * @param {Function} props.findDartById - Function to find dart by ID
 */
const GameHistory = ({
  sessions,
  expandedSessionId,
  hoveredSessionId,
  setHoveredSessionId,
  toggleSessionDetail,
  findDartById,
}) => {
  // Hooks
  const { userInfo, proMember } = useSelector((state) => state.user);
  const { t } = useTranslation();
  const handleDeleteSession = useSessionDeletion(userInfo);

  // Early return for empty sessions
  if (sessions.length === 0) {
    return (
      <div className="bg-secondary/80 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-lg w-full max-w-4xl mx-auto border border-primary/20">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-test">
            {t("history.gameHistory.gameSessions")}
          </h2>
          <span className="text-xs sm:text-sm text-gray-300">
            0 {t("history.gameHistory.sessions")}
          </span>
        </div>
        <div className="text-center py-8 sm:py-12">
          <p className="text-gray-300 mb-4">
            {t("history.gameHistory.noGameSessions")}
          </p>
          <p className="text-sm text-gray-400">
            {t("history.gameHistory.startPlaying")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-secondary/80 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-lg w-full max-w-4xl mx-auto border border-primary/20">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-semibold text-test">
          {t("history.gameHistory.gameSessions")}
        </h2>
        <span className="text-xs sm:text-sm text-gray-300">
          {sessions.length} {t("history.gameHistory.sessions")}
        </span>
      </div>

      {/* Sessions List */}
      <ul className="text-white text-sm space-y-3 sm:space-y-4">
        {sessions.map((session, index) => {
          const isExpanded = expandedSessionId === session.id;
          const usedDart = findDartById(session.dartsUsed);

          return (
            <SessionItem
              key={session.id}
              session={session}
              index={index}
              isExpanded={isExpanded}
              usedDart={usedDart}
              proMember={proMember}
              onToggle={() => toggleSessionDetail(session.id)}
              onDelete={handleDeleteSession}
            />
          );
        })}
      </ul>
    </div>
  );
};

export default GameHistory;
